import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function useFraudAnalysis(claimId, claimStatus, analyzedAt) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/fraud/results/${claimId}`);
      setAnalysis(res.data);
      return res.data;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  useEffect(() => { refresh(); }, [refresh, claimStatus, analyzedAt]);

  const running = analysis?.status === "processing" || analysis?.status === "queued";
  useEffect(() => {
    if (running && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const d = await refresh();
        if (d && d.status !== "processing" && d.status !== "queued") {
          clearInterval(pollRef.current); pollRef.current = null;
          if (d.status === "completed") toast.success(`Fraud analysis complete — score ${d.composite_score}`);
          else if (d.status === "failed") toast.error(d.error || "Fraud analysis failed");
        }
      }, 3000);
    }
    if (!running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [running, refresh]);

  const run = useCallback(async () => {
    setStarting(true);
    try {
      await axios.post(`${API}/fraud/analyze/${claimId}`);
      setAnalysis(prev => ({ ...(prev || {}), status: "queued" }));
      toast.info("Fraud analysis started");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start analysis");
    } finally {
      setStarting(false);
    }
  }, [claimId]);

  return { analysis, loading, running, starting, refresh, run };
}
